import { acceptanceParsingComparisonConfig, regexForParsing } from './constants';

/**
 * Extracts the properties from the user initiated request
 * @param {*} obj response object from response
 * @returns an array of properties
 */
export function extractProperties(obj, parentPath = '') {
  let properties = [];

  if (typeof obj !== 'object') {
    return properties;
  }
  
  for (let key in obj) {
    let value = obj[key];
    let jpath = parentPath ? (!isNaN(key) ? `${parentPath}[${key}]` : `${parentPath}.${key}`) : key;
    
    if (typeof value === 'object' && value !== null) {
      properties.push(...extractProperties(value, jpath));
    } else {
      properties.push({ label: key, id: key.toLowerCase(), path: jpath });
    }
  }
  
  properties = _updateDuplicates(properties);

  return properties;
}

/**
 * Transforms the user selected tags for API consumption
 * @param {*} tags an array of tags selected by the user
 * @returns an object containing tag values in a specified format
 */
export function sanitizeCustomTagsForRequest(tags) {
  const sanitizedTags = {};

  tags.forEach(tag => {
    const { label, id, path } = tag;
    sanitizedTags[label] = [
      {
        'type': 'JPath',
        'value': path
      }
    ];
  });

  return sanitizedTags;
}

/**
 * Transforms the returned tags for display in the UI
 * @param {*} tags an object of tags returned from the API
 * @returns an object containing tag values as label and id props
 */
export function sanitizeCustomTagsForDisplay(tags) {
  return Object.entries(tags).map(([key, value]) => {
    return {
      label: key,
      id: value[0].value.toLowerCase(),
      path: value[0].value
    };
  });
}

/**
 * Transforms the user configured acceptance parsing steps API consumption
 * @param {*} parsing parsing object
 * @returns an array of parsing steps
 */
export function sanitizeAcceptanceParsingForRequest(parsing) {
  const acceptanceParsing = [];

  parsing.forEach(item => {
    const { property, path, operator, value } = item;
    const requiresInteger = !isNaN(value) && (operator.value === '<' || operator.value === '>');
    const inputParsingValue = requiresInteger ? `return input.toLowerCase() ${operator.value} ${value}` : `return input.toLowerCase() ${operator.value} \"${value}\".toLowerCase()`;

    acceptanceParsing.push({
      'type': 'JPath',
      'value': (typeof property === 'object') ? property.path : path
    });
    
    acceptanceParsing.push({
      'type': 'JavaScript',
      'value': inputParsingValue
    });
  });

  return acceptanceParsing;
}

/**
 * Transforms the returned parsing steps for display in the UI
 * @param {*} parsing an array of parsing steps returned from the API
 * @returns an array of parsing steps
 */
export function sanitizeAcceptanceParsingForDisplay(parsing) {

  const operatorRegex = regexForParsing.operaterRegex;
  const valueRegex = regexForParsing.valueRegex;
  const valueRegexInt = regexForParsing.valueRegexInt;

  const operatorMatch = parsing[1].value.match(operatorRegex);
  const operator = operatorMatch[0];

  const valueMatch = parsing[1].value.match(valueRegex) || parsing[1].value.match(valueRegexInt);
  const value = valueMatch[1].trim();

  const pathArray = parsing[0].value.split('.');
  const label = pathArray?.length > 1 ? `${pathArray[pathArray.length - 2]} > ${pathArray.pop()}` : pathArray.pop();
  const operatorObject = acceptanceParsingComparisonConfig.options.find(op => op.value === operator);

  const acceptanceParsing = [
    {
      property: label,
      path: parsing[0].value,
      operator: {
        label: operatorObject.label,
        value: operatorObject.value
      },
      value: value
    }
  ];

  return acceptanceParsing;
}

/**
 * Transforms the returned score parsing for display in the UI
 * @param {*} parsing a score parsing array returned from the API
 * @returns an array of score parsing
 */
export function sanitizeScoreParsingForDisplay(parsing) {
  let scoreParsing = null;

  if (parsing && parsing.length) {
    const pathArray = parsing[0].value.split('.');
    const label = pathArray?.length > 1 ? `${pathArray[pathArray.length - 2]} > ${pathArray.pop()}` : pathArray.pop();
    scoreParsing = {
      id: parsing[0].value.toLowerCase(),
      label: label,
      path: parsing[0].value
    };
  }
  
  return scoreParsing;
}

/**
 * Transforms the user configured score parsing steps for API consumption
 * @param {*} parsing parsing object
 * @returns an array of parsing steps
 */
export function sanitizeScoreParsingForRequest(parsing) {
  const scoreParsing = [];

  if (parsing) {
    scoreParsing.push({
      'type': 'JPath',
      'value': parsing.path
    });
  }

  return scoreParsing;
}

/**
 * Update duplicate properties with the parent property in the label and id
 * @param {*} options selectable options of properties
 * @returns an updated array of options
 */
function _updateDuplicates(options) {
  const labelMap = {};

  return options.reduce((updatedOptions, item) => {
    const { label, id, path } = item;
    const pathArray = path.split('.');
    const parent = pathArray[pathArray.length - 2];
    const count = labelMap[label] || 0;
    labelMap[label] = count + 1;

    if (count > 0) {
      item.label = `${parent} > ${label}`;
      item.id = `${parent.toLowerCase()}:${id}`;
    }

    updatedOptions.push(item);
    return updatedOptions;
  }, []);
}